import {inject, Injectable} from '@angular/core';
import {WmWoundDocumentationEntry} from '../../interfaces/wound-management/wm-wound-documentation-entry';
import {SecureTable} from '../secure.table';
import {DefaultAdapter} from '../../adapters/default.adapter';
import {WmWoundDocumentationEntryFactory} from '../../factories/wound-management/wm-wound-documentation-entry-factory';
import {db} from '../db.service';
import {WmWoundImagesTable} from './wm-wound-images.table';
import {WmWoundAnswersTable} from './wm-wound-answers.table';


@Injectable({
  providedIn: 'root'
})
export class WmWoundDocumentationEntriesTable extends SecureTable<WmWoundDocumentationEntry> {

  private _wmWoundImagesTable: WmWoundImagesTable;
  private _wmWoundAnswersTable: WmWoundAnswersTable;

  constructor() {
    super(db.wmWoundDocumentationEntries);
    this._tableSyncSettings = {
      error: false,
      syncMode: 'default',
      dependencies: [],
      adapter: new DefaultAdapter<WmWoundDocumentationEntry>({
        api: ['wound-management', 'wound-documentation-entries'],
        fromObject: WmWoundDocumentationEntryFactory.fromObject,
        toRemote: WmWoundDocumentationEntryFactory.toRemote
      })
    };
    this._wmWoundImagesTable = inject(WmWoundImagesTable);
    this._wmWoundAnswersTable = inject(WmWoundAnswersTable);
  }

  /**
   *
   * @param wmWoundDocumentationUuid
   */
  public async removeByWmWoundDocumentationUuid(wmWoundDocumentationUuid: string): Promise<void> {
    const wmWoundAnswers =
      await this.table.where('wmWoundDocumentationUuid').equals(wmWoundDocumentationUuid).toArray();
    await this.removeMany(wmWoundAnswers.map((wmWoundAnswer) => wmWoundAnswer.uuid));
  }

  /**
   *
   * @param wmWoundDocumentationUuid
   */
  public async getByWmWoundDocumentationUuid(wmWoundDocumentationUuid: string): Promise<WmWoundDocumentationEntry[]> {
    return this.table.where('wmWoundDocumentationUuid')
      .equals(wmWoundDocumentationUuid).reverse().sortBy('created');
  }

  /**
   *
   * @param wmWoundDocumentationUuids
   */
  public async getByWmWoundDocumentationUuids(wmWoundDocumentationUuids: string[]): Promise<WmWoundDocumentationEntry[]> {
    return this.table.where('wmWoundDocumentationUuid').anyOf(wmWoundDocumentationUuids).toArray();
  }

  /**
   *
   */
  public override async isSynced(): Promise<boolean> {
    let isSynced = await super.isSynced();
    if(!isSynced) {
      return false;
    }
    isSynced = await this._wmWoundImagesTable.isSynced();
    if(!isSynced) {
      return false;
    }
    return await this._wmWoundAnswersTable.isSynced();
  }

  /**
   *
   * @param wmWoundDocumentationUuids
   */
  public async isSyncedByWmWoundDocumentationUuids(wmWoundDocumentationUuids: string[]): Promise<boolean> {
    const wmWoundDocumentationEntries =
      await this.getByWmWoundDocumentationUuids(wmWoundDocumentationUuids);
    return this.isSyncedByUuids(wmWoundDocumentationEntries.map(
      wmWoundDocumentationEntry => wmWoundDocumentationEntry.uuid
    ));
  }

  /**
   *
   */
  public override async isSyncedByUuids(wmWoundDocumentationEntryUuids: string[]): Promise<boolean> {
    const wmWoundImages =
      await this._wmWoundImagesTable.getByWmWoundDocumentationEntryUuids(wmWoundDocumentationEntryUuids);

    const isWmWoundImagesSynced =
      await this._wmWoundImagesTable.isSyncedByUuids(wmWoundImages.map(wmWoundImage => wmWoundImage.uuid));

    if(!isWmWoundImagesSynced) {
      return false;
    }

    const wmWoundAnswers =
      await this._wmWoundAnswersTable.getByWmWoundDocumentationEntryUuids(wmWoundDocumentationEntryUuids);

    return this._wmWoundAnswersTable.isSyncedByUuids(wmWoundAnswers.map(wmWoundAnswer => wmWoundAnswer.uuid));
  }

  /**
   *
   */
  public override async clear(force: boolean = false): Promise<void> {
    await super.clear(force);
    await this._wmWoundImagesTable.clear(force);
    await this._wmWoundAnswersTable.clear(force);
  }

  /**
   * TODO: Transaction
   *
   * @param itemUuids
   * @param force
   * @param withFiles
   */
  public override async removeMany(itemUuids: string[],
                                   force: boolean = false,
                                   withFiles: boolean = true): Promise<void> {

    const wmWoundImages =
      await this._wmWoundImagesTable.getByWmWoundDocumentationEntryUuids(itemUuids);

    if(withFiles) {
      await this._wmWoundImagesTable.deleteWmWoundImageFiles(wmWoundImages);
    }

    await db.transaction('rw',
      db.syncStatusItems,
      db.wmWoundDocumentationEntries,
      db.wmWoundAnswers,
      db.wmWoundImages,
      async () => {
        await this._wmWoundImagesTable.removeMany(
          wmWoundImages.map(_wmWoundImage => _wmWoundImage.uuid), force, false);

        const wmWoundAnswers =
          await this._wmWoundAnswersTable.getByWmWoundDocumentationEntryUuids(itemUuids);

        await this._wmWoundAnswersTable.removeMany(
          wmWoundAnswers.map(_wmWoundAnswer => _wmWoundAnswer.uuid),
          force
        );
        await super.removeMany(itemUuids, force);
      });
  }

}
